import React, { useCallback, useContext, useState } from 'react';
import * as R from 'ramda';
import { useTranslation } from 'react-i18next';
import { FlowConfigContext } from '../../../providers/FlowConfigContext';
import RequirementsPopup from './RequirementsPopup';
import { DataShareItem, DataShareItemRequirement } from './types';
import { GatekeeperNetworkServiceContext } from '../../../providers/GatekeeperNetworkService/GatekeeperNetworkServiceContext';

export const getCountriesList = (gknServiceData: any): DataShareItemRequirement[] => {
  const { bannedCountries, blockedCountries } = R.pathOr(
    { bannedCountries: [], blockedCountries: [] },
    ['constraints', 'public', 'location'],
    gknServiceData,
  );

  const mergedCountries = [...bannedCountries, ...blockedCountries];
  const dedupedCountries = Array.from(new Set(mergedCountries));

  const countriesList: DataShareItemRequirement[] = [];
  dedupedCountries.forEach((country) => {
    countriesList.push({
      title: `countries.country_${country}`,
    }) as unknown as DataShareItemRequirement;
  });

  return countriesList;
};

const PiiDataTypesToShare: React.FC = () => {
  const { t } = useTranslation();
  const { networkName } = useContext(FlowConfigContext);
  const { gknServiceData } = useContext(GatekeeperNetworkServiceContext);

  // This is hard-coded for now but the idea is to eventually get this data from the GKNS
  const getShareItems = useCallback(() => {
    const shareData: Array<DataShareItem> = [
      {
        itemType: 'screens.authorizePersonalInfo.idLabel',
        typeDescription: 'screens.authorizePersonalInfo.governmentLabel',
        typeDetails: [
          'screens.authorizePersonalInfo.documentRequireInfo',
          'screens.authorizePersonalInfo.documentRequirePhotos',
        ],
        requirements: {
          title: 'screens.authorizePersonalInfo.requirements.title',
          popupTitle: 'screens.authorizePersonalInfo.requirements.document.title',
          children: [
            {
              title: 'screens.authorizePersonalInfo.requirements.document.type',
              children: [
                { title: 'screens.authorizePersonalInfo.requirements.document.license' },
                { title: 'screens.authorizePersonalInfo.requirements.document.passport' },
              ],
            },
            {
              title: 'screens.authorizePersonalInfo.requirements.document.unsupportedCountries',
              children: getCountriesList(gknServiceData),
            },
          ],
        },
      },
      {
        itemType: 'screens.authorizePersonalInfo.emailLabel',
        typeDescription: 'screens.authorizePersonalInfo.emailRequireInfo',
      },
    ];
    return shareData;
  }, [gknServiceData]);

  return (
    <div className="w-full px-8">
      {getShareItems().map((item) => (
        <div className="mb-4" key={item.itemType}>
          <span className="text-sm font-light text-textTertiary">{t(item.itemType, { context: networkName })}</span>
          <div className="flex flex-col">
            <div className="flex flex-row">
              <span className="mr-2 text-md font-extrabold">{t(item.typeDescription, { context: networkName })}</span>

              {item.requirements && <RequirementsPopup requirements={item.requirements} />}
            </div>

            {item.typeDetails && (
              <ul className="list-outside list-disc ml-6">
                {item.typeDetails.map((detail) => (
                  <li className="text-primary" key={detail}>
                    <span className="text-sm text-textTertiary text-wrap">{t(detail, { context: networkName })}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default PiiDataTypesToShare;
